/**
 * /security/permissions — read-only permission catalog.
 *
 * Lists every permission grouped by namespace. Each row shows the key,
 * label, description, an is_destructive badge, and which roles grant it.
 * Supports namespace filtering + key search.
 *
 * The role→permission mapping is derived client-side: we fetch the role
 * list (GET /v1/admin/roles) then each role's detail (GET
 * /v1/admin/roles/:id) for its permission_keys. Roles are ≤10 so this
 * N+1 is cheap and keeps the backend surface unchanged.
 *
 * Gated on admin.list via the parent route.
 *
 * Spec: docs/superpowers/specs/2026-05-28-admin-rbac-design.md §8.4.
 */
import { useEffect, useMemo, useState } from 'react';
import { useApiUrl } from '@refinedev/core';
import { Search } from 'lucide-react';
import { adminFetch } from '@/providers/admin-api';

interface Permission {
  id: string;
  key: string;
  namespace: string;
  action: string;
  label: string;
  description: string;
  is_destructive: boolean;
}

interface RoleSummary {
  id: string;
  key: string;
  name: string;
}

interface RoleDetail {
  id: string;
  key: string;
  permission_keys: string[];
}

export function PermissionsCatalogPage() {
  const apiUrl = useApiUrl();
  const [permissions, setPermissions] = useState<Permission[]>([]);
  // permission key -> role keys that grant it.
  const [permToRoles, setPermToRoles] = useState<Record<string, string[]>>({});
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [nsFilter, setNsFilter] = useState<string>('all');
  const [search, setSearch] = useState('');

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const [perms, roleList] = await Promise.all([
          adminFetch(`${apiUrl}/v1/admin/permissions`).then(
            r => r.json() as Promise<Permission[]>,
          ),
          adminFetch(`${apiUrl}/v1/admin/roles`).then(
            r => r.json() as Promise<RoleSummary[]>,
          ),
        ]);
        if (cancelled) return;
        setPermissions(perms);

        const details = await Promise.all(
          roleList.map(role =>
            adminFetch(`${apiUrl}/v1/admin/roles/${role.id}`).then(
              r => r.json() as Promise<RoleDetail>,
            ),
          ),
        );
        if (cancelled) return;
        const map: Record<string, string[]> = {};
        for (const d of details) {
          for (const pk of d.permission_keys) {
            (map[pk] ??= []).push(d.key);
          }
        }
        for (const k of Object.keys(map)) map[k].sort();
        setPermToRoles(map);
      } catch (e: any) {
        if (!cancelled) setError(e?.message || 'Failed to load permissions');
      } finally {
        if (!cancelled) setIsLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [apiUrl]);

  const namespaces = useMemo(
    () => [...new Set(permissions.map(p => p.namespace))].sort(),
    [permissions],
  );

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    return permissions.filter(p => {
      if (nsFilter !== 'all' && p.namespace !== nsFilter) return false;
      if (
        q &&
        !p.key.toLowerCase().includes(q) &&
        !p.label.toLowerCase().includes(q)
      ) {
        return false;
      }
      return true;
    });
  }, [permissions, nsFilter, search]);

  const grouped = useMemo(() => {
    const byNs = new Map<string, Permission[]>();
    for (const p of filtered) {
      const arr = byNs.get(p.namespace) ?? [];
      arr.push(p);
      byNs.set(p.namespace, arr);
    }
    return [...byNs.entries()].sort((a, b) => a[0].localeCompare(b[0]));
  }, [filtered]);

  return (
    <div className="space-y-6">
      <div>
        <h2 className="text-2xl font-bold">Permissions</h2>
        <p className="text-muted-foreground">
          {permissions.length} permission{permissions.length === 1 ? '' : 's'}{' '}
          across {namespaces.length} namespace
          {namespaces.length === 1 ? '' : 's'} &middot; read-only catalog
        </p>
      </div>

      {error && (
        <div className="rounded-lg border border-destructive bg-destructive/10 p-4 text-sm text-destructive">
          {error}
        </div>
      )}

      <div className="flex flex-wrap items-center gap-3">
        <div className="relative">
          <Search className="absolute left-2.5 top-2.5 h-4 w-4 text-muted-foreground" />
          <input
            type="text"
            value={search}
            onChange={e => setSearch(e.target.value)}
            placeholder="Search by key or label…"
            className="rounded-md border bg-background py-2 pl-8 pr-3 text-sm"
          />
        </div>
        <select
          value={nsFilter}
          onChange={e => setNsFilter(e.target.value)}
          className="rounded-md border bg-background px-3 py-2 text-sm"
        >
          <option value="all">All namespaces</option>
          {namespaces.map(ns => (
            <option key={ns} value={ns}>
              {ns}
            </option>
          ))}
        </select>
      </div>

      {isLoading && permissions.length === 0 ? (
        <div className="rounded-lg border bg-card p-8 text-center text-sm text-muted-foreground">
          Loading permissions…
        </div>
      ) : grouped.length === 0 ? (
        <div className="rounded-lg border bg-card p-8 text-center text-sm text-muted-foreground">
          No permissions match your filters.
        </div>
      ) : (
        <div className="space-y-6">
          {grouped.map(([ns, perms]) => (
            <div key={ns} className="rounded-lg border bg-card">
              <div className="border-b bg-muted/50 px-4 py-3">
                <h3 className="text-sm font-semibold capitalize">{ns}</h3>
              </div>
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead>
                    <tr className="border-b text-left text-xs text-muted-foreground">
                      <th className="px-4 py-2 font-medium">Permission</th>
                      <th className="px-4 py-2 font-medium">Key</th>
                      <th className="px-4 py-2 font-medium">Granted to roles</th>
                    </tr>
                  </thead>
                  <tbody>
                    {perms.map(p => {
                      const roleKeys = permToRoles[p.key] ?? [];
                      return (
                        <tr key={p.id} className="border-b last:border-0">
                          <td className="px-4 py-3 text-sm">
                            <div className="flex items-center gap-2">
                              <span className="font-medium">{p.label}</span>
                              {p.is_destructive && (
                                <span className="inline-flex rounded-full bg-destructive/10 px-2 py-0.5 text-xs font-medium text-destructive">
                                  destructive
                                </span>
                              )}
                            </div>
                            {p.description && (
                              <div className="text-xs text-muted-foreground">
                                {p.description}
                              </div>
                            )}
                          </td>
                          <td className="px-4 py-3 text-sm">
                            <code className="text-xs">{p.key}</code>
                          </td>
                          <td className="px-4 py-3 text-sm">
                            {roleKeys.length === 0 ? (
                              <span className="text-xs italic text-muted-foreground">
                                no roles
                              </span>
                            ) : (
                              <div className="flex flex-wrap gap-1">
                                {roleKeys.map(rk => (
                                  <span
                                    key={rk}
                                    className={
                                      rk === 'super_admin'
                                        ? 'inline-flex rounded-full bg-primary/10 px-2 py-0.5 text-xs font-medium text-primary'
                                        : 'inline-flex rounded-full bg-muted px-2 py-0.5 text-xs font-medium'
                                    }
                                  >
                                    {rk}
                                  </span>
                                ))}
                              </div>
                            )}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
